import React, { useState, useEffect, useContext } from "react";
import { UserContext } from "../../../api/userContext";
import { loadEvents } from "../../../api/events";
import { getFavoriteEvents, addFavoriteEvents } from "../../../api/favoriteEvents";
import eventUtils from '../../../utils/eventUtils';
import { formatDateOnly, formatEventRecurrenceLabel, isSameCalendarDay } from '../../../utils/dateUtils';
import { truncateAtLastSpace } from '../../../utils/textUtils';
import { compareCategories, sortEventsByCategoryPriority } from '../../../utils/eventCategoryOrder';
import EventCardSkeleton from './EventCardSkeleton';
import NoEventsState from './NoEventsState';
import SearchBar from './SearchBar';
import FavoritesCarousel from './FavoritesCarousel';
import "../../../assets/css/animate.css";
import "./popularEventArea.css"; // Added import for custom styles

const ALL_CATEGORIES = "Tous";
const serverUrl = process.env.REACT_APP_SERVER_URL;

const PopularEventArea = ({ city: initialCity, distance: initialDistance, onSearch }) => {
  
  const { user } = useContext(UserContext);
  const [city, setCity] = useState(initialCity);
  const [distance, setDistance] = useState(initialDistance);
  const [restrictToCity, setRestrictToCity] = useState(false);
  const [events, setEvents] = useState([]);
  const [uniqueCategories, setUniqueCategories] = useState([]);
  const [filteredEvents, setFilteredEvents] = useState([]);
  const [activeCategory, setActiveCategory] = useState(null);
  const [favorites, setFavorites] = useState([]);
  const [loading, setLoading] = useState(true);
  // Show-more state
  const [visibleCount, setVisibleCount] = useState(9);

  // Handle search from SearchBar
  const handleSearch = (newCity, newDistance, newRestrictToCity = false) => {
    setCity(newCity);
    setDistance(newDistance);
    setRestrictToCity(Boolean(newRestrictToCity));
    if (onSearch) {
      onSearch(newCity, newDistance, Boolean(newRestrictToCity));
    }
  };

  // Reset visibleCount when list changes (city, distance, or category)
  useEffect(() => {
    setVisibleCount(9);
  }, [city, distance, restrictToCity, activeCategory, filteredEvents.length]);

  useEffect(() => {
    let isMounted = true;
    const fetchFavorites = async () => {
      try {
        if (!user) return;
        const favEvents = await getFavoriteEvents(user.uid);
        if (isMounted && favEvents) {
          setFavorites(favEvents || []);
        }
      } catch (error) {
        console.error('Error fetching favorites:', error);
      }
    };

    const loadComingEvents = async (city, distance, restrictToCity) => {
      try {
        const { eventsData } = await loadEvents(city, distance, undefined, true, restrictToCity);
        if (!isMounted) return;
        if (eventsData) {
          const orderedEvents = sortEventsByCategoryPriority(eventsData);
          setEvents(orderedEvents);
          const categories = orderedEvents.map(event => event.category);
          const uniqueCategories = [...new Set(categories)].sort(compareCategories);
          setUniqueCategories(uniqueCategories);
          const filteredEvents = activeCategory
            ? orderedEvents.filter(event => event.category === activeCategory)
            : orderedEvents;
          setFilteredEvents(filteredEvents);
        } else {
          setEvents([]);
          setFilteredEvents([]);
          setUniqueCategories([]);
        }
      } catch (error) {
        console.error('Failed to load events:', error);
        if (isMounted) {
          setEvents([]);
          setFilteredEvents([]);
          setUniqueCategories([]);
        }
      }
    };

    const init = async () => {
      setLoading(true);
      await loadComingEvents(city || null, distance, restrictToCity);
      await fetchFavorites();
      if (isMounted) setLoading(false);
    };

    init();
    return () => { isMounted = false; };
  }, [city, distance, restrictToCity, user, activeCategory]);

  const scrollTop = () => (
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    })
  )

  const handleFavoriteClick = async (eventId) => {

    let updatedFavorites;
    try {
      if (favorites.map(fav => fav.eventId).includes(eventId)) {
        updatedFavorites = favorites.filter(event => event.eventId !== eventId);
      } else {
        updatedFavorites = [...favorites, { eventId, favoritedAt: Date.now() }];
      }
      setFavorites(updatedFavorites);

      if (!user) {
        return;
      }
    
      const success = await addFavoriteEvents(user.uid, updatedFavorites);
      if (!success) {
        throw new Error('Failed to update favorite');
      }
    } catch (error) {
      console.error('Error adding favorite:', error);
      return;
    }
  };

  const filterEvents = (category) => {
    setActiveCategory(category);
    const filteredEvents = category
      ? events.filter(event => event.category === category)
      : events;
    setFilteredEvents(filteredEvents);
  };
  
  const imageStyle = {
    width: '100%',
    height: 'auto',
    maxHeight: '200px', // Adjust this value as needed for responsiveness
    objectFit: 'cover'
  };

  const getEventImage = (event) => {
    return eventUtils.getDefaultImage(event);
  };

  const getBackgroundColor = (index) => {
    // 10 couleurs différentes avec transparence
    const colors = [
      'rgba(47, 172, 102, 0.6)',   // vert
      'rgba(206, 20, 70, 0.6)',    // rouge
      'rgba(47, 137, 252, 0.6)',   // bleu
      'rgba(255, 153, 0, 0.6)',    // orange
      'rgba(156, 39, 176, 0.6)',   // violet
      'rgba(0, 150, 136, 0.6)',    // turquoise
      'rgba(233, 30, 99, 0.6)',    // rose
      'rgba(255, 193, 7, 0.6)',    // jaune
      'rgba(103, 58, 183, 0.6)',   // indigo
      'rgba(96, 125, 139, 0.6)'    // gris bleuté
    ];
    
    // Utiliser le modulo pour répéter les couleurs tous les 10 éléments
    return colors[index % 10];
  };

  const getLightBackgroundColor = (index) => {
    // 10 couleurs différentes avec transparence
    const colors = [
      'rgba(47, 172, 102, 0.2)',   // vert
      'rgba(206, 20, 70, 0.2)',    // rouge
      'rgba(47, 137, 252, 0.2)',   // bleu
      'rgba(255, 153, 0, 0.2)',    // orange
      'rgba(156, 39, 176, 0.2)',   // violet
      'rgba(0, 150, 136, 0.2)',    // turquoise
      'rgba(233, 30, 99, 0.2)',    // rose
      'rgba(255, 193, 7, 0.2)',    // jaune
      'rgba(103, 58, 183, 0.2)',   // indigo
      'rgba(96, 125, 139, 0.2)'    // gris bleuté
    ];
    
    // Utiliser le modulo pour répéter les couleurs tous les 10 éléments
    return colors[index % 10];
  };
    
  // Navigate to details (used for non-Link clickable zones)
  const navigateToDetails = (id) => {
    scrollTop();
    window.location.href = `${process.env.PUBLIC_URL}/event-details?eid=${id}`;
  };

  return (
    <>
      
      {/* ===============  Event Area start  =============== */}
      <div className="event-area">
        {loading ? (
          <div className="container position-relative">
            <div className="row">
              <div className="col-lg-12">
                <div className="section-head">
                  <h3>évènements à venir</h3>
                </div>
              </div>
            </div>
            <SearchBar onSearch={handleSearch} />
            <div className="row">
              {/* Render 6 skeleton cards to simulate the loading state */}
              {[...Array(6)].map((_, index) => (
                <EventCardSkeleton key={index} />
              ))}
            </div>
          </div>
        ) : (
          <div className="container position-relative">
            <div className="row">
              <div className="col-lg-12">
                <div className="section-head">
                  <h3>évènements à venir</h3>
                </div>
              </div>
            </div>
            
            <SearchBar onSearch={handleSearch} />

            {/* Favorites Carousel - only shown for logged in users with favorites */}
            <FavoritesCarousel />
              
            <div className="row">
              <div className="col-lg-12">
                <div className="event-category-buttons">
                  <ul
                    className="nav nav-pills mb-2"
                    id="pills-tab"
                    role="tablist"
                  >
                      
                    <li className="nav-item" role="presentation">
                      <button
                        onClick={() => filterEvents(null)}
                        className={`nav-link ${activeCategory === null ? 'active' : ''}`}
                        id="pills-tab1"
                        data-bs-toggle="pill"
                        data-bs-target="#pills-event1"
                        type="button"
                        role="tab"
                        aria-controls="pills-event1"
                        aria-selected="true">
                        {ALL_CATEGORIES}<span>{events?.length}</span>
                      </button>
                    </li>
                    {uniqueCategories.map((category, index) => (
                          
                      <li key={`${category}-${index}`} className="nav-item" role="presentation">
                        <button
                          className={`nav-link ${activeCategory === category ? 'active' : ''}`}
                          onClick={() => filterEvents(category)}
                          id="pills-tab1"
                          data-bs-toggle="pill"
                          data-bs-target="#pills-event1"
                          type="button"
                          role="tab"
                          aria-controls="pills-event1"
                          aria-selected="true">
                          {category} <span>{events.filter(event => event.category === category).length}</span>
                        </button>
                      </li>
                    ))}
                      
                  </ul>
                    
                </div>
              </div>
              <div className="col-lg-12">
                <div className="all-event-cards">
                  <div className="tab-content" id="pills-tabContent">
                    <div
                      className="tab-pane fade show active"
                      id="pills-event1"
                      role="tabpanel"
                      aria-labelledby="pills-tab1">
                      <div className="row">
                        {filteredEvents.length === 0 ? (
                          <div className="col-lg-12">
                            <NoEventsState city={city} />
                          </div>
                        ) : (
                          // Only show the first 'visibleCount' events
                          filteredEvents.slice(0, visibleCount).map((event, index) => {
                            const recurrenceLabel = formatEventRecurrenceLabel(event);
                            return (
                            <div
                              key={`${event.id}-${index}`}
                              className="col-lg-4 col-md-6 mb-4 wow fadeInUp animated"
                              data-wow-delay="200ms"
                              data-wow-duration="1500ms">
                              <div 
                                className="event-card-md event-card-clickable h-100"
                                onClick={(e) => {
                                  // Don't navigate if clicking on share icons, favorite, or social links
                                  if (e.target.closest('.event-share-icons') || 
                                      e.target.closest('.favorite-icon') ||
                                      e.target.closest('a[href^="https://"]')) {
                                    return;
                                  }
                                  navigateToDetails(event.id);
                                }}
                              >
                                <div className="event-thumb" style={{ position: 'relative' }}>
                                  {/* Floating Date Badge - Top Left */}
                                  <div className="event-date-badge">
                                    <span className="date-day">{new Date(event.date).getDate()}</span>
                                    <span className="date-month">{new Date(event.date).toLocaleDateString('fr-FR', { month: 'short' }).toUpperCase()}</span>
                                    {event.endDate && !isSameCalendarDay(event.date, event.endDate) && (
                                      <>
                                        <span className="date-separator">→</span>
                                        <span className="date-day">{new Date(event.endDate).getDate()}</span>
                                        <span className="date-month">{new Date(event.endDate).toLocaleDateString('fr-FR', { month: 'short' }).toUpperCase()}</span>
                                      </>
                                    )}
                                  </div>

                                  {/* Favorite Button - Top Right */}
                                  {user &&
                                    <div
                                      className="event-favorite"
                                      style={{ background: getBackgroundColor(index), zIndex: 10 }}
                                    >
                                      <span
                                        style={{ cursor: 'pointer' }}
                                        className="favorite-icon"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleFavoriteClick(event.id);
                                        }}
                                      >
                                        <i className={favorites.map(fav => fav.eventId).includes(event.id) ? "bi bi-heart-fill" : "bi bi-heart"} />
                                      </span>
                                    </div>
                                  }

                                  {/* Image */}
                                  <img
                                    src={getEventImage(event)}
                                    alt={event.title || 'Event'}
                                    style={imageStyle}
                                  />

                                  {/* Overlay */}
                                  <div
                                    className="event-overlay"
                                    style={{ background: getLightBackgroundColor(index), zIndex: 2 }}
                                  />

                                  {/* Category Label - Bottom Center Full Width */}
                                  <div
                                    className="event-lavel"
                                    style={{ background: getBackgroundColor(index), zIndex: 3 }}
                                  >
                                    <i className="bi bi-diagram-3" />
                                    <span>{event.category}</span>
                                  </div>

                                  {/* Title block */}
                                  <div
                                    className="event-title"
                                    style={{ background: getBackgroundColor(index) }}
                                  >
                                    <h4>{event.title.length > 100 ? truncateAtLastSpace(event.title, 100) : event.title}</h4>
                                  </div>
                                </div>
                                <div className="event-content">
                                  <div className="event-info">
                                    <div className="event-date">
                                      <span className="event-date-text">
                                        <i className="bi bi-calendar2-week" />{" "}
                                        {event.endDate
                                          ? (
                                            isSameCalendarDay(event.date, event.endDate)
                                              ? formatDateOnly(event.date)
                                              : <>
                                                {`Du ${formatDateOnly(event.date)}`}
                                                <br />
                                                <span className="endDate">{`Au ${formatDateOnly(event.endDate)}`}</span>
                                              </>
                                          )
                                          : formatDateOnly(event.date)
                                        }
                                        {recurrenceLabel && (
                                          <>
                                            <br />
                                            <span className="endDate">{recurrenceLabel}</span>
                                          </>
                                        )}
                                      </span>
                                    </div>
                                    <div className="event-location">
                                      <span className="event-location-text">
                                        <i className="bi bi-geo-alt" />
                                        {event.location.cityName}{event.location.postalCode && ` ${event.location.postalCode}`}
                                      </span>
                                    </div>
                                  </div>
                                  {/* <h5 className="event-title">
                                        <Link
                                          onClick={scrollTop}
                                          to={`${process.env.PUBLIC_URL}/event-details?eid=${event.id}`}>
                                          {event.title}
                                        </Link>
                                      </h5> */}
                                  <h6 className="event-speaker">
                                    <p>{event.speaker ? `Intervenant : ` : ""}<span>{event.speaker || "\u00A0"}</span></p>
                                  </h6>
                                  <h3 className="event-organizedby">
                                    <p>{event.organizer ? `organisé par : ` : ""}<span>{event.organizer || "\u00A0"}</span></p>
                                  </h3>
                                  <div className="event-bottom">
                                    <div className="event-readme">
                                      <span className="event-details-link">
                                        Détails <i className="bi bi-arrow-right"></i>
                                      </span>
                                    </div>
                                    <div className="event-share-icons" onClick={(e) => e.stopPropagation()}>
                                      <ul className="share-options">
                                        <li>
                                          <a href={`https://www.facebook.com/sharer/sharer.php?u=${serverUrl}/event-details?eid=${event.id}`} target="_blank" rel="noreferrer">
                                            <i className="fab fa-facebook-f" />
                                          </a>
                                        </li>
                                        <li>
                                          <a href={`https://www.instagram.com/?url=${serverUrl}/event-details?eid=${event.id}`} target="_blank" rel="noreferrer">
                                            <i className="fab fa-instagram" />
                                          </a>
                                        </li>
                                        <li>
                                          <a href={`https://twitter.com/intent/tweet?url=${serverUrl}/event-details?eid=${event.id}&text=${event.title}`} target="_blank" rel="noreferrer">
                                            <i className="fa-brands fa-x-twitter" />
                                          </a>
                                        </li>
                                      </ul>
                                      <div className="share-btn">
                                        <i className="bi bi-share" />
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                            );
                          })
                        )}
                      </div>

                      {/* Show more button */}
                      {filteredEvents.length > visibleCount && (
                        <div className="text-center mt-3">
                          <button
                            type="button"
                            className="btn btn-outline-success"
                            onClick={() => setVisibleCount(c => c + 9)}
                          >
                            Voir plus <i className="bi bi-chevron-down ms-1" />
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div >
      {/* ===============  Event Area end  =============== */}
    </>
  )
};

export default PopularEventArea;
